import React, { useEffect, useMemo, useState } from 'react';
import { useGetAskRequestAttachment } from 'hooks/ask-requests';
import Loading from 'components/Loading';
import ExcelPreview from 'presentation/components/ExcelPreview/ExcelPreview';
import SVGPreviewFromBlob from '../../SvgPreviewFromBlob/SvgPreviewFromBlob';
import classNames from 'classnames';

import './MessageAttachmentPreview.scss';
import { Attachment } from 'presentation/components/Queue/Chat/Chat';

type MessageAttachmentPreviewProps = {
  attachment: Attachment;
  size?: 'normal' | 'thumbnail';
  isSelected?: boolean;
  handleClickPreview?: (attachment: Attachment) => void;
  handleSetDownloadedBlob?: (blob: Blob) => void;
};

const MessageAttachmentPreview: React.FC<MessageAttachmentPreviewProps> = ({
  attachment,
  size,
  isSelected,
  handleSetDownloadedBlob,
  handleClickPreview,
}) => {
  const { attachmentId, fileType } = attachment;
  const { data: blob } = useGetAskRequestAttachment(attachmentId, fileType || '');

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleChangeFilePreview = () => {
    if (handleClickPreview) {
      handleClickPreview(attachment);
      handleSetDownloadedBlob?.(blob!);
    }
  };

  const imagePreview = useMemo(() => {
    if (!previewUrl) {
      return (
        <div className="attachment-modal__preview--viewer--placeholder no-preview">
          <Loading />
        </div>
      );
    }

    if (!fileType) {
      return <div className="attachment-modal__preview--viewer--placeholder unknown-type">Unknown file type</div>;
    }

    const fileTypeLower = fileType.toLowerCase();
    const imagesTypes = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'tiff'];
    const isImage = imagesTypes.includes(fileTypeLower);
    const isSvg = fileTypeLower === 'svg';
    const isVideo = fileTypeLower === 'mp4' || fileTypeLower === 'mov' || fileTypeLower === 'avi';
    const isAudio = fileTypeLower === 'mp3' || fileTypeLower === 'wav' || fileTypeLower === 'ogg';
    const isText = fileTypeLower === 'txt' || fileTypeLower === 'csv' || fileTypeLower === 'json';
    const isHtml = fileTypeLower === 'html' || fileTypeLower === 'htm';
    const isMarkdown = fileTypeLower === 'md' || fileTypeLower === 'markdown';
    const isExcel = fileTypeLower === 'xls' || fileTypeLower === 'xlsx';
    const isWord = fileTypeLower === 'doc' || fileTypeLower === 'docx';
    const isPdf = fileTypeLower === 'pdf';
    const isZip = fileTypeLower === 'zip' || fileTypeLower === 'rar' || fileTypeLower === 'tar';
    const isPowerPoint = fileTypeLower === 'ppt' || fileTypeLower === 'pptx';
    const isCsv = fileTypeLower === 'csv';

    if (isSvg) {
      return (
        <div className={classNames('attachment-modal__preview--viewer--placeholder svg-container', size)}>
          <SVGPreviewFromBlob blobUrl={previewUrl} />
        </div>
      );
    }

    if (isImage) {
      return (
        <div className={classNames('attachment-modal__preview--viewer--placeholder image-container', size)}>
          <div>
            <img
              src={previewUrl}
              alt="Attachment"
              className="attachment-modal__image"
              onError={e => console.error('Main image render failed', e)}
            />
          </div>
        </div>
      );
    }

    if (isVideo) {
      return (
        <div className="attachment-modal__preview--viewer--placeholder video-container">
          <video controls className="attachment-modal__video">
            <source src={previewUrl} type={`video/${fileTypeLower}`} />
          </video>
        </div>
      );
    }

    if (isAudio) {
      return (
        <div className="attachment-modal__preview--viewer--placeholder audio-container">
          <audio controls className="attachment-modal__audio">
            <source src={previewUrl} type={`audio/${fileTypeLower}`} />
          </audio>
        </div>
      );
    }

    if (isPdf) {
      return (
        <div className="attachment-modal__preview--viewer--placeholder pdf-container" onClick={handleChangeFilePreview}>
          <iframe src={previewUrl} title="PDF Preview" className={classNames('attachment-modal__text-iframe', size)} />
        </div>
      );
    }

    if (isExcel || isCsv) {
      return (
        <div className={classNames('attachment-modal__preview--viewer--placeholder excel-container', size)}>
          <ExcelPreview blob={blob!} />
        </div>
      );
    }

    if (isText || isHtml || isMarkdown) {
      return (
        <div className="attachment-modal__preview--viewer--placeholder text-container">
          <iframe
            src={previewUrl}
            title="Text Preview"
            className={classNames('attachment-modal__text-iframe', size)}
            height={400}
            width={400}
          />
        </div>
      );
    }

    if (isExcel || isWord || isPowerPoint || isZip) {
      return (
        <div className="attachment-modal__preview--viewer--placeholder doc-xls-container">
          <p className="attachment-modal__no-preview-text">Preview not available for {fileTypeLower} files</p>
        </div>
      );
    }

    return (
      <div className="attachment-modal__preview--viewer--placeholder unsupported-type">
        <p className="attachment-modal__unsupported-text">Unsupported file type: {fileType}</p>
      </div>
    );
  }, [previewUrl]);

  // Try multiple rendering methods
  useEffect(() => {
    if (!blob) return;
    handleSetDownloadedBlob?.(blob);
    const methods: { [key: string]: string | null } = {};

    // Method 1: Direct URL object
    try {
      methods.objectUrl = URL.createObjectURL(blob);
    } catch (e) {
      methods.objectUrlError = e instanceof Error ? e.message : 'Unknown error';
    }

    // Method 2: FileReader
    const reader = new FileReader();
    reader.onload = () => {
      try {
        methods.fileReader = reader.result as string;
      } catch (e) {
        console.warn('FileReader error:', e);
      }
    };
    reader.onerror = () => {};
    reader.readAsDataURL(blob);

    // Set initial methods (will be updated by FileReader onload)

    // Set main preview URL
    setPreviewUrl(methods.objectUrl);

    // Cleanup
    return () => {
      if (methods.objectUrl) URL.revokeObjectURL(methods.objectUrl);
    };
  }, [blob, attachment]);

  return (
    <div
      className={classNames('attachment-modal__preview', { selected: isSelected }, size)}
      onClick={handleChangeFilePreview}
    >
      {imagePreview}
    </div>
  );
};

export default MessageAttachmentPreview;
